// Google Analytics 4 loader, gated by analytics consent via Google
// Consent Mode v2.
//
// Why Consent Mode v2 and not "skip loading entirely":
//   Consent Mode v2 is Google's official answer to "the user said no
//   to analytics" — gtag still loads but every signal is suppressed
//   client-side until you call gtag('consent', 'update', { ... }). This
//   means we can react to consent changes without forcing a page reload,
//   AND we get the small bonus of "modeled conversions" if the user
//   later toggles consent on. Net cost: ~30 KB of gtag.js loaded even
//   when denied — but the network round-trip happens once and cookies
//   are not set in 'denied' mode.
//
// Required defaults (Google's spec, all set to 'denied' until consent):
//   analytics_storage   — gates _ga, _gid, _gat cookies
//   ad_storage          — gates DoubleClick/Floodlight cookies
//   ad_user_data        — controls sending user identifiers in ad events
//   ad_personalization  — controls personalization features
//
// We map our two consent buckets to these:
//   analytics consent → analytics_storage
//   marketing consent → ad_storage + ad_user_data + ad_personalization
//
// Events we fire from this app:
//   page_view      — automatic on init
//   begin_checkout — fired explicitly from Payment.jsx
//   purchase       — fired explicitly from DownloadPage on first
//                    transition into status='paid'

import { hasAnalyticsConsent, hasMarketingConsent, subscribe } from './consent.js';

const MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;

let initialised = false;

function ensureDataLayer() {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      // gtag is a thin wrapper around dataLayer.push — same shape every
      // call: arguments object pushed verbatim.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
}

function applyConsentState() {
  if (typeof window === 'undefined') return;
  const analytics = hasAnalyticsConsent() ? 'granted' : 'denied';
  const marketing = hasMarketingConsent() ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  });
}

function injectGtag() {
  if (initialised) return;
  if (typeof window === 'undefined') return;
  if (!MEASUREMENT_ID) return;
  initialised = true;

  ensureDataLayer();

  // Default-deny everything BEFORE the gtag.js script tag is injected.
  // Required by Consent Mode v2 — anything that fires before the default
  // call is treated as un-consented and not retroactively cleaned up.
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID, {
    // anonymize_ip is implicit on GA4 (no opt-out), but explicit doesn't hurt.
    anonymize_ip: true,
  });

  // Reflect the user's current choice immediately if they had previously
  // consented on this device.
  applyConsentState();

  // Inject the script tag last so the consent + config calls above land
  // in the dataLayer queue before gtag.js drains it.
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(s);
}

/**
 * Fire an explicit GA4 event. Safe to call regardless of consent state —
 * gtag will drop signals when analytics_storage is 'denied'. The wrapper
 * guards on env-var presence so we don't throw when GA4 is intentionally
 * unconfigured (e.g. local dev).
 *
 * @param {string} eventName
 * @param {Record<string, unknown>=} params
 */
export function gaTrackEvent(eventName, params) {
  if (typeof window === 'undefined') return;
  if (!MEASUREMENT_ID) return;
  if (!initialised) injectGtag();
  if (typeof window.gtag !== 'function') return;
  if (params) {
    window.gtag('event', eventName, params);
  } else {
    window.gtag('event', eventName);
  }
}

if (typeof window !== 'undefined') {
  if (!MEASUREMENT_ID && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn('[crayo] VITE_GA4_MEASUREMENT_ID unset; GA4 disabled.');
  }

  if (MEASUREMENT_ID) {
    // Always inject — Consent Mode v2 keeps the SDK inert until granted.
    // This way we capture modeled conversions and react to consent flips
    // without a reload.
    injectGtag();
  }

  subscribe(() => {
    applyConsentState();
  });

  // Expose for legacy components.
  window.crayoGa = { gaTrackEvent };
}
